import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { addFastClick } from '~/scripts/utils/utils.js';
import { getSectionId } from '~/scripts/utils/shopify-utils.js';
import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('base-overlay', false);

const SHOW_PAGE_ATTRIBUTE = 'data-show-page';
const PAGE_ATTRIBUTE = 'data-page';

export default class BaseOverlay extends HTMLElement {
    constructor({ openOnSectionSelect = false } = {}) {
        super();
        this._delegatedCleanupFunctions = [];
        this._themeEditorCleanupFunctions = [];
        this._instanceListeners = [];
        this._initialized = false;

        this.openOnSectionSelect = openOnSectionSelect;
    }

    static get observedAttributes() {
        return ['open'];
    }

    connectedCallback() {
        this._init();
    }

    disconnectedCallback() {
        this.destroy();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        // Guard against attribute changes before initialization
        if (!this._initialized) return;

        if (name === 'open') {
            if (newValue !== null && oldValue === null) {
                this._handleOpen();
            } else if (newValue === null && oldValue !== null) {
                this._handleClose();
            }
        }
    }

    _init() {
        // Guard against multiple initializations
        if (this._initialized) return;

        this.sectionId = getSectionId(this);
        this.setupToggles();
        this.setupDelegatedShowPage();

        debug.log('Initialized with section:', { sectionid: this.sectionId, this: this });

        this.setupEventListeners();

        if (this.sectionId && window.Shopify?.designMode) {
            this.bindThemeEditorEvents();
        }

        this._initialized = true;
    }

    setupToggles() {
        const toggleAttribute = this.getToggleAttribute();
        if (!toggleAttribute) return;

        this.toggleSelector = `[${toggleAttribute}]`;

        this.setupDelegatedToggle();
    }

    setupDelegatedToggle() {
        const cleanup = this.delegateClickDocument(this.toggleSelector, (match, evt) => {
            evt.preventDefault();
            this.toggle();
        });
        this._delegatedCleanupFunctions.push(cleanup);
    }

    setupDelegatedShowPage() {
        const cleanup = this.delegateClick(`[${SHOW_PAGE_ATTRIBUTE}]`, (match, evt) => {
            // Only handle the event if the clicked element is within this overlay
            if (!this.contains(match)) {
                return;
            }
            evt.preventDefault();
            this.showPage(match.dataset.showPage);
        });
        this._delegatedCleanupFunctions.push(cleanup);
    }

    setupEventListeners() {
        // Store bound handlers for proper cleanup
        this._handleKeyUp = this.handleKeyUp.bind(this);
        this._handleOverlayClick = this.handleOverlayClick.bind(this);

        this.addEventListener('keyup', this._handleKeyUp);
        this._instanceListeners.push({ event: 'keyup', handler: this._handleKeyUp });

        if (this.shouldCloseOnOutsideClick()) {
            this.addEventListener('click', this._handleOverlayClick);
            this._instanceListeners.push({ event: 'click', handler: this._handleOverlayClick });
        }
    }

    handleKeyUp(evt) {
        if (evt.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    handleOverlayClick(evt) {
        const content = this.getContentElement();
        if (content && !content.contains(evt.target) && evt.target === this) {
            this.close();
        }
    }

    get isOpen() {
        return this.getAttribute('open') !== null;
    }

    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    open() {
        if (this.isOpen) return;
        this.setAttribute('open', '');
    }

    close() {
        if (!this.isOpen) return;
        this.removeAttribute('open');
    }

    _handleOpen() {
        const scrollTarget = this.getScrollLockTarget();
        if (scrollTarget) {
            disableBodyScroll(scrollTarget);
        }

        this.onOpen();
        this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    _handleClose() {
        const scrollTarget = this.getScrollLockTarget();
        if (scrollTarget) {
            enableBodyScroll(scrollTarget);
        }

        this.onClose();
        this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }

    destroy() {
        this.close();

        // Clean up instance listeners
        this._instanceListeners.forEach(({ event, handler }) => {
            this.removeEventListener(event, handler);
        });
        this._instanceListeners = [];

        // Clean up delegated listeners
        this._delegatedCleanupFunctions.forEach((cleanup) => cleanup());
        this._delegatedCleanupFunctions = [];

        // Clean up theme editor listeners
        this._themeEditorCleanupFunctions.forEach((cleanup) => cleanup());
        this._themeEditorCleanupFunctions = [];
    }

    /**
     * Utility method for delegated event handling using fast click (scoped to overlay)
     * @param {string} selector - CSS selector to match against
     * @param {function} callback - Callback function (match, event) => {}
     * @returns {function} Cleanup function to remove the listener
     */
    delegateClick(selector, callback) {
        const handler = (e) => {
            const match = e.target.closest(selector);
            if (match && this.contains(match)) {
                callback(match, e);
            }
        };

        // Use addFastClick for optimized touch/mouse handling
        return addFastClick(this, handler);
    }

    /**
     * Utility method for document-level delegated event handling using fast click
     * @param {string} selector - CSS selector to match against
     * @param {function} callback - Callback function (match, event) => {}
     * @returns {function} Cleanup function to remove the listener
     */
    delegateClickDocument(selector, callback) {
        const handler = (e) => {
            const match = e.target.closest(selector);
            if (match && document.contains(match)) {
                callback(match, e);
            }
        };

        // Use addFastClick on document for global toggle elements
        return addFastClick(document.documentElement, handler);
    }

    // Methods to be overridden by subclasses
    getToggleAttribute() {
        return this.dataset.toggleAttribute;
    }

    getContentElement() {
        return this.querySelector('.overlay__content, .drawer__inner, .modal__content');
    }

    getScrollLockTarget() {
        return this.getContentElement() || this;
    }

    shouldCloseOnOutsideClick() {
        return false;
    }

    useDelegation() {
        return this.hasAttribute('data-use-delegation');
    }

    shouldOpenOnSectionSelect() {
        return false;
    }

    onOpen() {
        // Override in subclass
    }

    onClose() {
        // Override in subclass
    }

    bindThemeEditorEvents() {
        // Store bound handlers for cleanup
        const handlers = {
            sectionLoad: (e) => {
                if (e.detail.sectionId === this.sectionId) {
                    this.onThemeEditorLoad(e);
                }
            },
            sectionSelect: (e) => {
                if (e.detail.sectionId === this.sectionId) {
                    debug.log('Section select:', {
                        event: e,
                        this: this,
                        shouldOpenOnSectionSelect: this.shouldOpenOnSectionSelect()
                    });
                    if (this.shouldOpenOnSectionSelect()) {
                        this.open();
                    }
                }
            },
            blockSelect: (e) => {
                if (e.detail.sectionId === this.sectionId) {
                    this.onThemeEditorBlockSelect(e);
                    this.open();
                }
            },
            sectionDeselect: (e) => {
                if (e.detail.sectionId === this.sectionId) {
                    this.close();
                }
            },
            blockDeselect: (e) => {
                if (e.detail.sectionId === this.sectionId) {
                    this.onThemeEditorBlockDeselect(e);
                }
            }
        };

        // Add listeners and store cleanup functions
        document.addEventListener('shopify:section:load', handlers.sectionLoad);
        document.addEventListener('shopify:section:select', handlers.sectionSelect);
        document.addEventListener('shopify:block:select', handlers.blockSelect);
        document.addEventListener('shopify:section:deselect', handlers.sectionDeselect);
        document.addEventListener('shopify:block:deselect', handlers.blockDeselect);

        // Store cleanup function
        this._themeEditorCleanupFunctions.push(() => {
            document.removeEventListener('shopify:section:load', handlers.sectionLoad);
            document.removeEventListener('shopify:section:select', handlers.sectionSelect);
            document.removeEventListener('shopify:block:select', handlers.blockSelect);
            document.removeEventListener('shopify:section:deselect', handlers.sectionDeselect);
            document.removeEventListener('shopify:block:deselect', handlers.blockDeselect);
        });
    }

    onThemeEditorLoad(event) {
        // Override in subclass if needed
    }

    onThemeEditorBlockSelect(event) {
        // Override in subclass if needed
    }

    onThemeEditorBlockDeselect(event) {
        // Override in subclass if needed
    }

    /**
     * Show a specific page within the overlay
     * @param {string} page - The page identifier to show
     * @returns {boolean} - Returns true if page was found and shown, false otherwise
     */
    showPage(page) {
        const showingPageEl = this.querySelector(`[${PAGE_ATTRIBUTE}="${page}"]`);

        if (!showingPageEl) {
            console.warn(`Page with ${PAGE_ATTRIBUTE}="${page}" not found in overlay`);
            return false;
        }

        // Hide all pages
        this.querySelectorAll(`[${PAGE_ATTRIBUTE}]`).forEach((pageEl) => {
            pageEl.classList.add('js-hidden');
        });

        // Show target page
        showingPageEl.classList.remove('js-hidden');

        return true;
    }
}

customElements.define('base-overlay', BaseOverlay);
